// CRM 2.0 · M12-F3: Project › Template Mapping, part "CRM 2.0 bookings → our templates", below the part
// "Older bookings → Anulom" (today's mapping, which old-flow bookings keep using and which this part never changes).
// Shown for every project: the links can be set before the project switches to CRM 2.0 (the M1-F5 health check
// needs them); until then the part says "Used after this project switches to CRM 2.0".
// Each row: a tower (or all towers) + a FarVision payment plan (or all plans) -> a Live agreement template.
// "Who gets what" shows every tower × plan and the template it gets as saved (backend: services/documents/links.js).
// kind ALLOTMENT_LETTER (H-017): the same part on Project › Allotment Letter Template, the project or a tower (no plan
// column, no FarVision call), saved through the same /template-links API.
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import cls from "../../Pages/Templates/Templates.module.css";
import { getTemplateLinks, saveTemplateLinks } from "../../Services/Templates";
import { getAllPlansByProjectFromFarvision } from "../../Services/PaymentPlans";
import { getProjectByProjectName } from "../../Services/SustenanceService";
import { canDo } from "../../Pages/Templates/templateCommon";
import { errorTost, successTost } from "../../Services/Toast";

const ALL = "";
const rowsOf = (links) => links.map((l) => ({ wingId: l.wingId || ALL, fvPaymentPlanId: l.fvPaymentPlanId == null ? ALL : String(l.fvPaymentPlanId), docTemplateId: l.docTemplateId }));
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// what each kind's part says; perPlan: linked per FarVision payment plan (the agreement) or only per tower
const KINDS = {
  AGREEMENT: {
    perPlan: true, title: "CRM 2.0 bookings → our templates", column: "Agreement template", saved: "CRM 2.0 template links saved",
    intro: "For bookings made under CRM 2.0 in this project: the agreement is made from our approved template. Older bookings keep the Anulom mapping above. Matching order: tower + plan, then plan, then tower, then the whole project.",
    noLive: "No Live agreement template yet: approve one in Templates first.",
    whoHint: "Every tower and plan of the project with the template its bookings get, as saved. “No template”: an agreement for such a booking stops with “No agreement template linked for this tower and plan”. A linked template that is no longer Live is never printed from: relink it here.",
  },
  ALLOTMENT_LETTER: {
    perPlan: false, title: "CRM 2.0 bookings → our allotment letter", column: "Allotment letter template", saved: "CRM 2.0 allotment letter links saved",
    intro: "For bookings made under CRM 2.0 in this project: the allotment letter is made from our approved template after Final Review approval. Older bookings keep the allotment letter template above. A tower's own row wins over the whole project.",
    noLive: "No Live allotment letter template yet: approve one in Templates first.",
    whoHint: "Every tower of the project with the allotment letter its bookings get, as saved. “No template”: Final Review approval of such a booking is refused with “Allotment letter can't be made: No allotment letter template linked for this project or wing”. A linked template that is no longer Live is never printed from: relink it here.",
  },
};

export default function Crm2TemplateLinks({ projectId, kind = "AGREEMENT" }) {
  const k = KINDS[kind] || KINDS.AGREEMENT;
  const user = useSelector((state) => state.Auth.user);
  const allowed = canDo(user, "Template Linking");
  const [plans, setPlans] = useState(null); // [{ id, name }]
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [saved, setSaved] = useState([]);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  useEffect(() => {
    if (!projectId || !allowed) return;
    let gone = false;
    (async () => {
      setProblem(null);
      try {
        // the project's FarVision payment plans, read the same way as the Anulom section above (only a kind linked per plan)
        let list = [];
        if (k.perPlan) {
          const project = await getProjectByProjectName(projectId);
          const resp = await getAllPlansByProjectFromFarvision(project?.data?.[0]?.buId);
          list = (resp?.data?.result || []).map((p) => ({ id: Number(p.Id), name: p.Description || `Plan ${p.Id}` }));
        }
        const d = await getTemplateLinks(projectId, list, kind);
        if (gone) return;
        setPlans(list);
        show(d, list);
      } catch (e) {
        if (!gone) setProblem(e.message);
      }
    })();
    return () => { gone = true; };
  }, [projectId, allowed, kind]);

  function show(d, list) {
    setData(d);
    const r = rowsOf(d.links);
    setSaved(r);
    // nothing linked yet: one row per plan for all towers (a kind without plans: one row for the whole project), to fill in
    const blank = k.perPlan ? (list || []).map((p) => ({ wingId: ALL, fvPaymentPlanId: String(p.id), docTemplateId: "" }))
      : [{ wingId: ALL, fvPaymentPlanId: ALL, docTemplateId: "" }];
    setRows(r.length ? r : blank);
  }

  const change = (i, key, value) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [key]: value } : r)));
  const remove = (i) => setRows((rs) => rs.filter((_, j) => j !== i));
  const add = () => setRows((rs) => [...rs, { wingId: ALL, fvPaymentPlanId: ALL, docTemplateId: "" }]);

  async function save() {
    if (rows.some((r) => !r.docTemplateId)) return errorTost("Choose a template for every row, or remove the row");
    setBusy(true);
    try {
      const d = await saveTemplateLinks(projectId, rows.map((r) => ({
        wingId: r.wingId || null, fvPaymentPlanId: r.fvPaymentPlanId === ALL ? null : Number(r.fvPaymentPlanId), docTemplateId: r.docTemplateId,
      })), plans, kind);
      show(d, plans);
      successTost(k.saved);
    } catch (e) {
      errorTost(e.message);
    } finally {
      setBusy(false);
    }
  }

  const head = (
    <h2 className={cls.panelTitle}>
      {k.title}
      {data && !data.project.isCrm2 && <span className={cls.hint} style={{ fontWeight: 400, marginLeft: ".8rem" }}>· Used after this project switches to CRM 2.0</span>}
    </h2>
  );
  if (!allowed) {
    return <div className={cls.page}><div className={cls.panel}>{head}<p className={cls.hint}>You need Templates › Template Linking access to see or change the CRM 2.0 template links.</p></div></div>;
  }
  if (problem) return <div className={cls.page}><div className={cls.panel}>{head}<p className={cls.hint}>{problem}</p></div></div>;
  if (!data) return <div className={cls.page}><div className={cls.panel}>{head}<p className={cls.hint}>Loading…</p></div></div>;

  const planName = new Map((plans || []).map((p) => [String(p.id), p.name]));
  const live = data.liveTemplates;
  const dirty = !same(rows, saved);
  // a saved row whose template is no longer Live (shown, so it can be changed)
  const notLive = new Map(data.links.filter((l) => l.status !== "LIVE").map((l) => [l.docTemplateId, `${l.template || "Template"} (${String(l.status || "").toLowerCase()})`]));

  return (
    <div className={cls.page} style={{ padding: "2.4rem 0 0" }}>
      <div className={cls.panel}>
        {head}
        <p className={cls.hint} style={{ marginTop: 0 }}>{k.intro}</p>
        {!live.length && <p className={cls.hint} style={{ color: "var(--warn)" }}>{k.noLive}</p>}
        <div className={cls.tblw}>
          <table className={cls.tbl}>
            <thead><tr><th>Tower</th>{k.perPlan && <th>Payment plan</th>}<th>{k.column}</th><th className={cls.last} /></tr></thead>
            <tbody>
              {!rows.length && <tr><td colSpan={k.perPlan ? 4 : 3} className={cls.empty}>No rows. Use + Add a row.</td></tr>}
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>
                    <select className={cls.sel} value={r.wingId} onChange={(e) => change(i, "wingId", e.target.value)} aria-label="Tower">
                      <option value={ALL}>All towers</option>
                      {data.wings.map((w) => <option key={w.wingId} value={w.wingId}>{w.name}</option>)}
                    </select>
                  </td>
                  {k.perPlan && (
                    <td>
                      <select className={cls.sel} value={r.fvPaymentPlanId} onChange={(e) => change(i, "fvPaymentPlanId", e.target.value)} aria-label="Payment plan">
                        <option value={ALL}>All plans</option>
                        {(plans || []).map((p) => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
                        {r.fvPaymentPlanId !== ALL && !planName.has(r.fvPaymentPlanId) && <option value={r.fvPaymentPlanId}>Plan {r.fvPaymentPlanId} (not in FarVision now)</option>}
                      </select>
                    </td>
                  )}
                  <td>
                    <select className={cls.sel} value={r.docTemplateId} onChange={(e) => change(i, "docTemplateId", e.target.value)} aria-label={k.column}>
                      <option value="">Choose a template…</option>
                      {live.map((t) => <option key={t.docTemplateId} value={t.docTemplateId}>{t.template}</option>)}
                      {notLive.has(r.docTemplateId) && <option value={r.docTemplateId}>{notLive.get(r.docTemplateId)}</option>}
                    </select>
                  </td>
                  <td className={cls.last}><button className={`${cls.btn} ${cls.danger}`} onClick={() => remove(i)}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", gap: ".8rem", marginTop: "1.6rem", flexWrap: "wrap" }}>
          <button className={cls.btn} onClick={add}>+ Add a row</button>
          <span style={{ flex: 1 }} />
          {dirty && <button className={cls.btn} disabled={busy} onClick={() => setRows(saved)}>Cancel changes</button>}
          <button className={`${cls.btn} ${cls.primary}`} disabled={busy || !dirty} onClick={save}>Save</button>
        </div>

        <h2 className={cls.panelTitle} style={{ marginTop: "3.2rem" }}>Who gets what</h2>
        <p className={cls.hint} style={{ marginTop: 0 }}>{k.whoHint}</p>
        <div className={cls.tblw}>
          <table className={cls.tbl}>
            <thead><tr><th>Tower</th>{k.perPlan && <th>Payment plan</th>}<th>Template</th></tr></thead>
            <tbody>
              {!data.whoGetsWhat.length && (
                <tr><td colSpan={k.perPlan ? 3 : 2} className={cls.empty}>{k.perPlan ? "No payment plans found for this project in FarVision." : "No towers found for this project."}</td></tr>
              )}
              {data.whoGetsWhat.map((w) => (
                <tr key={`${w.wingId}|${w.fvPaymentPlanId}`}>
                  <td>{w.towerName}</td>
                  {k.perPlan && <td>{w.planName}</td>}
                  <td className={w.template ? cls.main : undefined} style={w.template ? undefined : { color: "var(--err)" }}>{w.template || w.problem}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
